import React, { useEffect } from "react";
import { Box, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation } from "react-router";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { useForm } from "../../../provider/Form/FormProvider";
import { getCaseLink } from "../../../services/productService";
import TextContainer from "../../Container/TextContainer";
import ContractData from "../ContractLaw/Summary/ContractData";
import DebtCollectionAgencyData from "../ContractLaw/Summary/DebtCollectionAgencyData";
import InsuranceData from "../Settlement/Summary/InsuranceData";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import EmailVerificationModal from "../../FormInfoModal/EmailVerificationModal";
import summaryStyle from "../ContractLaw/Summary/summaryStyle";
import SummaryDetailsItem from "../Common/SummaryDetailsItem/SummaryDetailsItem";
import { scrollToAnchor } from "../../../theme/commonStyle";
import ContractDescription from "../ContractLaw/Summary/ContractDescription";
import { OpposingPartyData } from "./Summary/OpposingPartyData";
import { isFlexServiceProduct } from "../../../services/advocardService";

type SummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

const Summary = ({ product, submitProps }: SummaryProps) => {
  const location = useLocation();
  const stateField = location.state?.field;
  const { values, handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  return (
    <Paper elevation={4}>
      <TextContainer>
        <Typography variant={"h4"} sx={summaryStyle.headline}>
          Ihre Angaben
        </Typography>
        <Grid container>
          <Box id={"contractData"} sx={summaryStyle.scrollToAnchor} />
          <ContractData caseData={product} />
          {product.caseCategory !== "advisory" && (
            <>
              <Box id={"contractDetails"} sx={scrollToAnchor} />
              {product.contractPartyType === "tenant" && <SummaryDetailsItem fieldPath={"reasonForDispute"} caseData={product} />}
              <SummaryDetailsItem fieldPath={"housingDeficienciesDescriptionClient"} caseData={product} />
              <SummaryDetailsItem fieldPath={"previousBasicRent"} caseData={product} />
              <SummaryDetailsItem fieldPath={"previousRentIncludingHeating"} caseData={product} />
              <SummaryDetailsItem fieldPath={"futureBasicRent"} caseData={product} />
              <SummaryDetailsItem fieldPath={"futureRentIncludingHeating"} caseData={product} />
              <SummaryDetailsItem fieldPath={"depositAmount"} caseData={product} />
              <SummaryDetailsItem fieldPath={"cancellationReceivedDate"} caseData={product} />

              <Box id={"contractualPartnerData"} sx={summaryStyle.scrollToAnchor} />
              <OpposingPartyData formData={values} />
              <Grid sx={summaryStyle.subHeadline} size={12}>
                <Typography variant={"h5"}>Adresse des Objekts</Typography>
              </Grid>
              <SummaryDetailsItem fieldPath={"propertyAddress.streetAddress"} caseData={product} />
              <SummaryDetailsItem fieldPath={"propertyAddress.postalCode"} caseData={product} />
              <SummaryDetailsItem fieldPath={"propertyAddress.addressLocality"} caseData={product} />
              <SummaryDetailsItem fieldPath={"propertyAddress.addressAddition"} caseData={product} />
            </>
          )}
          {product.caseCategory === "defendClaim" && product.defenseProcessStatus === "debtCollection" && (
            <>
              <Box id={"debtCollectionAgencyData"} sx={summaryStyle.scrollToAnchor} />
              <DebtCollectionAgencyData title={"Angaben zum Inkassobüro"} formData={values} />
            </>
          )}
          <Box id={"contractDescription"} sx={summaryStyle.scrollToAnchor} />
          <ContractDescription title={"Beschreibung der Situation"} formData={values} />
          <Box id={"insuranceData"} sx={summaryStyle.scrollToAnchor} />
          {!isFlexServiceProduct(product) && <InsuranceData title={"Versicherung"} product={product} />}
        </Grid>
        <form onSubmit={handleSubmit}>
          <FormSubmit
            {...submitProps}
            hideBackButton={false}
            backLink={getCaseLink(product) + "/antrag/vertrag"}
            backText={"Alles bearbeiten"}
            nextText={"Angaben speichern"}
          />
        </form>
      </TextContainer>
      <EmailVerificationModal />
    </Paper>
  );
};

export default Summary;
